import React, { useState, useEffect, useRef } from 'react';
import './InputForm.css';
import { searchAddressSuggestions } from '../services/api';

const InputForm = ({ onSubmit, loading, defaultSettings = {} }) => {
  const [points, setPoints] = useState([{ id: '1', address: '', lat: null, lng: null }]);
  const [suggestions, setSuggestions] = useState({});
  // Removendo índice ativo que não está sendo usado corretamente
  // e pode estar contribuindo para o bug de sincronização entre pontos
  const [lastProcessedPointId, setLastProcessedPointId] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const suggestionsRefs = useRef({});

  const handleAddPoint = () => {
    const newId = String(points.length + 1);
    setPoints([...points, { id: newId, address: '', lat: null, lng: null }]);
  };

  const handleRemovePoint = (id) => {
    if (points.length > 1) {
      setPoints(points.filter(point => point.id !== id));
      // Remover sugestões associadas ao ponto removido
      const newSuggestions = { ...suggestions };
      delete newSuggestions[id];
      setSuggestions(newSuggestions);
    }
  };
  
  // Mover ponto para cima na lista (prioridade mais alta)
  const handleMoveUp = (index) => {
    if (index > 0) {
      const newPoints = [...points];
      [newPoints[index], newPoints[index - 1]] = [newPoints[index - 1], newPoints[index]];
      setPoints(newPoints);
    }
  };
  
  // Mover ponto para baixo na lista (prioridade mais baixa)
  const handleMoveDown = (index) => {
    if (index < points.length - 1) {
      const newPoints = [...points];
      [newPoints[index], newPoints[index + 1]] = [newPoints[index + 1], newPoints[index]];
      setPoints(newPoints);
    }
  };

  const handlePointChange = (id, field, value) => {
    // Armazenar o ID do ponto atual sendo alterado para evitar conflitos entre pontos
    const currentEditingId = id;
    
    // Atualizar o ponto
    setPoints(points.map(point => {
      if (point.id === currentEditingId) {
        const updatedPoint = { ...point, [field]: value };
        
        // Se o campo for endereço, limpar as coordenadas para evitar conflitos
        if (field === 'address') {
          updatedPoint.lat = null;
          updatedPoint.lng = null;
          
          // Configurar um delay antes de buscar sugestões para evitar requisições excessivas
          if (typingTimeout) {
            clearTimeout(typingTimeout);
          }
          
          const newTimeout = setTimeout(() => {
            // Ao buscar sugestões, passar o ID do ponto específico
            fetchSuggestions(currentEditingId, value);
          }, 500);
          
          setTypingTimeout(newTimeout);
          
          // Registrar o último ponto processado
          setLastProcessedPointId(currentEditingId);
        }
        
        return updatedPoint;
      }
      return point;
    }));
  };

  const fetchSuggestions = async (pointId, query) => {
    // Guardar uma referência ao ID do ponto para o qual estamos buscando sugestões
    const currentPointId = pointId;
    console.log(`Buscando sugestões para ponto ${currentPointId}: "${query}"`);
    
    if (query.trim().length > 2) {
      try {
        // Adicionar a cidade/estado padrão à consulta se estiver configurada
        let enhancedQuery = query;
        
        if (defaultSettings?.defaultCity && !query.includes(defaultSettings.defaultCity)) {
          console.log(`Adicionando cidade padrão: ${defaultSettings.defaultCity}`);
          enhancedQuery += `, ${defaultSettings.defaultCity}`;
        }
        
        if (defaultSettings?.defaultState && !query.includes(defaultSettings.defaultState)) {
          console.log(`Adicionando estado padrão: ${defaultSettings.defaultState}`);
          enhancedQuery += `, ${defaultSettings.defaultState}`;
        }
        
        if (defaultSettings?.defaultCountry && !query.includes(defaultSettings.defaultCountry)) {
          console.log(`Adicionando país padrão: ${defaultSettings.defaultCountry}`);
          enhancedQuery += `, ${defaultSettings.defaultCountry}`;
        }
        
        console.log(`Query final para Mapbox: "${enhancedQuery}"`);
        const results = await searchAddressSuggestions(enhancedQuery);
        
        // Só atualizar sugestões se ainda estivermos interessados neste ponto específico
        if (currentPointId === lastProcessedPointId) {
          setSuggestions(prev => {
            const newSuggestions = { ...prev };
            // Limpar sugestões anteriores para todos os pontos para evitar confusão
            Object.keys(newSuggestions).forEach(key => {
              if (key !== currentPointId) {
                newSuggestions[key] = [];
              }
            });
            newSuggestions[currentPointId] = results;
            return newSuggestions;
          });
          
          console.log(`Atualizando sugestões para o ponto ${currentPointId}`);
        } else {
          console.log(`Ignorando sugestões para o ponto ${currentPointId} - não é mais o ponto ativo`);
        }
      } catch (error) {
        console.error(`Erro ao buscar sugestões para ponto ${currentPointId}:`, error);
      }
    } else {
      // Limpar sugestões se a consulta for muito curta
      // Só atualizar se for o último ponto processado
      if (currentPointId === lastProcessedPointId) {
        setSuggestions(prev => ({
          ...prev,
          [currentPointId]: []
        }));
      }
    }
  };

  const handleSuggestionClick = (pointId, suggestion) => {
    // Armazenar explicitamente o pointId e a sugestão para evitar problemas de closure
    const selectedPointId = pointId;
    const selectedSuggestion = {...suggestion};
    
    setPoints(points.map(point => {
      if (point.id === selectedPointId) {
        return {
          ...point,
          address: selectedSuggestion.placeName,
          lng: selectedSuggestion.coordinates[0],
          lat: selectedSuggestion.coordinates[1]
        };
      }
      return point;
    }));
    
    // Limpar todas as sugestões após selecionar uma
    setSuggestions({});
  };

  const handleOutsideClick = (e) => {
    // Verificar se o clique foi fora dos componentes de sugestão
    let clickedOutside = true;
    Object.keys(suggestionsRefs.current).forEach(key => {
      if (
        suggestionsRefs.current[key] && 
        suggestionsRefs.current[key].contains(e.target)
      ) {
        clickedOutside = false;
      }
    });
    
    if (clickedOutside) {
      // Limpar todas as sugestões
      setSuggestions({});
    }
  };

  useEffect(() => {
    // Adicionar listener para fechar sugestões ao clicar fora
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar entrada
    const validPoints = points.filter(point => 
      (point.address.trim() !== '' && point.lat !== null && point.lng !== null) ||
      (point.address.trim() !== '' && point.geocoded !== false)
    );
    
    if (validPoints.length === 0) {
      alert('Adicione pelo menos um ponto com endereço válido.');
      return;
    }
    
    // Enviar dados (apenas pontos, os agentes serão fornecidos pelo componente pai)
    onSubmit({ points: validPoints });
  };

  return (
    <form className="input-form" onSubmit={handleSubmit}>
      <div className="form-section">
        <h2>Pontos de Atendimento</h2>
        <p className="form-description">
          Adicione os pontos de atendimento por ordem de prioridade. Utilize os botões para reordenar se necessário.
        </p>
        
        {points.map((point, index) => (
          <div key={point.id} className="form-row">
            <div className="point-order-number">{index + 1}</div>
            <div className="address-input-container">
              <input
                type="text"
                placeholder={`Endereço ${defaultSettings.defaultCity ? `(em ${defaultSettings.defaultCity})` : ''}`}
                value={point.address}
                onChange={(e) => handlePointChange(point.id, 'address', e.target.value)}
                disabled={loading}
              />
              
              {suggestions[point.id] && suggestions[point.id].length > 0 && (
                <div 
                  className="suggestions-container"
                  ref={el => suggestionsRefs.current[point.id] = el}
                >
                  {suggestions[point.id].map((suggIndex, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(point.id, suggIndex)}
                    >
                      {suggIndex.placeName}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="point-actions">
              {/* Botões de reordenação - só exibir após o primeiro ponto */}
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => handleMoveUp(index)}
                  disabled={loading || index === 0}
                  className="reorder-button up-button"
                  title="Aumentar prioridade"
                >
                  ↑
                </button>
              )}
              
              {index < points.length - 1 && (
                <button
                  type="button"
                  onClick={() => handleMoveDown(index)}
                  disabled={loading || index === points.length - 1}
                  className="reorder-button down-button"
                  title="Diminuir prioridade"
                >
                  ↓
                </button>
              )}
              
              <button
                type="button"
                onClick={() => handleRemovePoint(point.id)}
                disabled={points.length === 1 || loading}
                className="remove-button"
                title="Remover ponto"
              >
                ×
              </button>
            </div>
          </div>
        ))}
        
        <button
          type="button"
          onClick={handleAddPoint}
          disabled={loading}
          className="add-button"
        >
          Adicionar Ponto
        </button>
      </div>
      
      <div className="form-section">
        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Otimizando...' : 'Otimizar Rotas'}
        </button>
      </div>
    </form>
  );
};

export default InputForm;